import { useCallback, useMemo, useState } from 'react';

import { enUS } from 'date-fns/locale';
import dayjs from 'dayjs';
import { CalendarIcon } from 'lucide-react';
import { Control, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// Monday as week start (1 = Monday, 0 = Sunday) per European standard
const WEEK_START_DAY = 1;
const DEFAULT_DATE_FORMAT = 'MMM D, YYYY';
// Sunday = 0, Saturday = 6
const WEEKEND_DAYS = [0, 6];

interface DatePickerProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  hideLabel?: boolean;
  dateFormat?: string;
  disabled?: (date: Date) => boolean;
  inputDisabled?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  /**
   * Whether to disable weekends (Saturday and Sunday) in the date picker.
   * @default false
   */
  disableWeekends?: boolean;
  /**
   * Calendar caption style. "dropdown" adds month + year selects so users can
   * jump to a distant date without paging through arrows (mirrors the
   * date-range calendar's OPS-1522 behavior).
   * @default 'label'
   */
  captionLayout?: 'label' | 'dropdown';
  /**
   * Whether a date must stay selected. When true, clicking the selected day keeps it
   * instead of clearing the field.
   * @default false
   */
  required?: boolean;
}

/** Years reachable in each direction when `captionLayout="dropdown"`. */
const YEAR_DROPDOWN_RANGE = 20;

interface DateValidationParams {
  date: Date;
  isWeekend: (date: Date) => boolean;
  customDisabled?: (date: Date) => boolean;
  shouldDisableWeekends: boolean;
}

/**
 * Creates locale configuration with Monday as first day of week.
 * Overrides default US locale (Sunday start) for international users.
 */
const createLocaleConfig = () => ({
  ...enUS,
  options: {
    ...enUS.options,
    weekStartsOn: WEEK_START_DAY as 0 | 1 | 2 | 3 | 4 | 5 | 6,
  },
});

/**
 * Combines optional weekend blocking with custom date validation rules.
 * Weekends can be disabled via the disableWeekends prop (defaults to false).
 */
const validateDate = ({
  date,
  isWeekend,
  customDisabled,
  shouldDisableWeekends,
}: DateValidationParams): boolean => {
  const weekendDisabled = shouldDisableWeekends && isWeekend(date);
  return weekendDisabled || (customDisabled ? customDisabled(date) : false);
};

export const DatePicker = <T extends FieldValues>({
  name,
  control,
  label,
  hideLabel = false,
  dateFormat = DEFAULT_DATE_FORMAT,
  disabled: customDisabled,
  inputDisabled = false,
  placeholder,
  isLoading = false,
  disableWeekends = false,
  captionLayout = 'label',
  required = false,
}: DatePickerProps<T>) => {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const locale = useMemo(() => createLocaleConfig(), []);

  const finalLabel = label || t('datePicker.selectDate');
  const finalPlaceholder = placeholder || t('datePicker.pickDate');

  // Utility function to check if a date is a weekend
  const isWeekend = useCallback((date: Date): boolean => {
    return WEEKEND_DAYS.includes(date.getDay());
  }, []);

  const isDateDisabled = useCallback(
    (date: Date): boolean => {
      return validateDate({
        date,
        isWeekend,
        customDisabled,
        shouldDisableWeekends: disableWeekends,
      });
    },
    [isWeekend, customDisabled, disableWeekends]
  );

  // Auto-close popover after selection for better UX
  // Normalize date to UTC midnight to avoid timezone issues
  const handleDateSelect = useCallback(
    (field: any) => {
      return (date: Date | undefined) => {
        if (date) {
          // Create a new date at UTC midnight to avoid timezone issues
          // If user selects "Nov 26, 2025", we want "2025-11-26T00:00:00.000Z"
          const utcDate = new Date(
            Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
          );
          field.onChange(utcDate);
        } else if (!required) {
          field.onChange(date);
        }
        setIsOpen(false);
      };
    },
    [required]
  );

  const formatSelectedDate = useCallback(
    (date: Date): string => {
      return dayjs(date).format(dateFormat);
    },
    [dateFormat]
  );

  // Accessibility: provides screen reader context about what date is being selected
  const getAriaLabel = useCallback((): string => {
    return t('datePicker.selectLabel', { label: finalLabel.toLowerCase() });
  }, [t, finalLabel]);

  // Animated loading component for inside the button
  const LoadingContent = () => <span className="animate-pulse">{t('loading')}</span>;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-col">
          {finalLabel && !hideLabel && <FormLabel htmlFor={name}>{finalLabel}</FormLabel>}

          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  id={name}
                  type="button"
                  variant="outline"
                  disabled={inputDisabled || isLoading}
                  aria-label={getAriaLabel()}
                  aria-expanded={isOpen}
                  aria-haspopup="dialog"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    'pl-3 pr-3',
                    (!field.value || isLoading) && 'text-muted-foreground',
                    'disabled:border-input disabled:opacity-100 disabled:bg-muted disabled:pointer-events-none'
                  )}
                >
                  <span className="flex-1">
                    {isLoading ? (
                      <LoadingContent />
                    ) : field.value ? (
                      formatSelectedDate(field.value)
                    ) : (
                      finalPlaceholder
                    )}
                  </span>
                  <CalendarIcon
                    className="ml-2 h-4 w-4 flex-shrink-0 opacity-50"
                    aria-hidden="true"
                  />
                </Button>
              </FormControl>
            </PopoverTrigger>

            <PopoverContent className="w-auto p-0" align="start" side="bottom">
              <Calendar
                mode="single"
                selected={field.value}
                onSelect={handleDateSelect(field)}
                disabled={isDateDisabled}
                defaultMonth={field.value}
                locale={locale}
                weekStartsOn={WEEK_START_DAY}
                showOutsideDays
                fixedWeeks
                captionLayout={captionLayout}
                startMonth={
                  captionLayout === 'dropdown'
                    ? new Date(new Date().getFullYear() - YEAR_DROPDOWN_RANGE, 0)
                    : undefined
                }
                endMonth={
                  captionLayout === 'dropdown'
                    ? new Date(new Date().getFullYear() + YEAR_DROPDOWN_RANGE, 11)
                    : undefined
                }
              />
            </PopoverContent>
          </Popover>

          <FormMessage />
        </FormItem>
      )}
    />
  );
};
